import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ImageIcon } from '@phosphor-icons/react/dist/csr/Image';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { UploadSimpleIcon } from '@phosphor-icons/react/dist/csr/UploadSimple';

import { Button } from '../../design-system/primitives/Button/Button';
import { FileField } from '../../design-system/primitives/FileField/FileField';
import { Combobox } from '../../design-system/primitives/Combobox/Combobox';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { mediaApi, mediaUrl, type CaseMediaRow, type MediaKind } from '../../lib/api/media';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './CaseMedia.module.css';

/**
 * The Photos panel on the case detail screen (M-17, Task 5) — the gallery a
 * case's uploaded evidence renders as, and the control staff use to add one
 * on a patient's behalf. A sibling of `CaseRequests`/`CaseMessages`: its own
 * load state, its own failure banner, mounted unconditionally beside them.
 *
 * ## Viewing is NEVER gated — not by permission, not by `isClosed`
 *
 * `GET /cases/{case}/media` carries `can:viewAny` on `CaseRecord` — the
 * identical gate `CaseDetail.tsx`'s own `casesApi.show()` already required
 * to reach this screen at all, so there is no narrower "can see photos"
 * permission to check here (unlike `CaseRequests`'s `request.view` or
 * `CaseReplacements`'s `canReadReplacements`). `CaseDetail.tsx` renders this
 * component alongside its other panels regardless of `isClosed` — M-15c's
 * review found the PDF download hidden behind that exact gate, and a closed
 * case's photos are the ones most likely to be wanted for the physical
 * file.
 *
 * ## Uploading is gated on `case.update`, the caller's job
 *
 * `POST /cases/{case}/media` carries `can:update`. `canUpload` is a prop —
 * `CaseDetail` already computes `permissions.includes('case.update')` for
 * its own Edit control (`canUpdate`), so this is the same boolean, not a
 * second read of the same permission string.
 *
 * ## Every retrieval is audited — a deliberate, reviewed cost
 *
 * `GET /case-media/{uuid}` (`mediaUrl`, used both as the plain `<img src>`
 * and as the full-resolution link's `href`) writes an audit row on every
 * hit, so a gallery of N photos costs N rows per render of this panel, and
 * opening the full-resolution link costs one more. That is spec's own
 * ruling for this route, not an oversight — this component adds no caching
 * or lazy-loading to reduce it.
 *
 * ## A failed upload keeps the chosen kind, on purpose
 *
 * `kind` is left exactly as the agent set it when `mediaApi.upload` throws —
 * clearing it back to the default would silently re-file the next retry
 * under the wrong kind, and a picker that resets itself on failure reads as
 * having "worked" when it did not.
 *
 * ## Fix round 1 (review findings F-1, F-2, F-4 — see the report's own section)
 *
 * - **F-1**: a successful upload used to leave the native, uncontrolled
 *   `<input type="file">` still holding the file — its own chrome kept
 *   showing the filename while `.fileName` read "No file chosen", and
 *   re-selecting the SAME file fired no `change` event at all (the input's
 *   `value` was unchanged), so the button stayed disabled forever for that
 *   file. One photo legitimately serves two kinds (`sensor_photo` AND
 *   `serial_number_photo`), so re-uploading the same file under a second
 *   kind is the ordinary flow, not a corner case. `fileInputRef` now clears
 *   `.value` on success — the same fix `CodeUploadPanel.tsx` needed the
 *   unused `fileInputRef` it already carries for.
 * - **F-2**: a bare `catch` rendered one generic sentence for every failure,
 *   including the API's 10 MB / mimetype 422s (`StoreCaseMediaRequest`,
 *   spec D-6) — telling an agent to retry the one thing guaranteed to fail
 *   again. `describeFailure` below is `CodeUploadPanel.tsx`'s own function,
 *   copied rather than reinvented: branch on `error.code` for the four
 *   known non-field failures, otherwise surface the API's own first field
 *   message (`error.details`), and rethrow anything that is not an
 *   `ApiError` at all instead of swallowing it.
 * - **F-4**: a photo was previously visible only as a cropped 160×120 tile
 *   with nothing clickable — exactly the failure spec D-7's refusal to
 *   re-encode uploads exists to prevent (a compressed serial number is
 *   unreadable, and reading it is the only reason that photo exists). Each
 *   tile is now wrapped in a link to the same authenticated `mediaUrl`,
 *   opening in a new tab — the browser is a perfectly good full-resolution
 *   viewer, and the cookie-authenticated `<img>` reasoning applies
 *   identically to a plain navigation. No lightbox: the fix stays a single
 *   `<a>`. `width`/`height` on the `<img>` stop the layout shift a
 *   full-resolution file previously caused while painting a 120px tall
 *   tile.
 */

export interface CaseMediaProps {
  caseUuid: string;
  canUpload: boolean;
}

type LoadStatus = 'loading' | 'ready' | 'error';

/**
 * Every `MediaKind` value, exhaustively — one per `request.*` SMS template.
 *
 * NINE, WHICH IS WHY THE CONTROL BELOW IS A `Combobox` AND NOT A `Select`.
 * It was four, and this comment used to note that four was "well under" the
 * handoff's ~8 line. Nine is over it, and `Select` says so out loud in dev:
 * "past it a native select is a scroll-and-hunt control. Use Combobox, which
 * filters." Taking that at its word rather than raising the cap is the whole
 * reason the cap is enforced in code.
 */
const KIND_OPTIONS: readonly MediaKind[] = [
  'sensor_photo',
  'error_photo',
  'receipt_photo',
  'serial_number_photo',
  'event_log',
  'wrong_sms',
  'wasfati',
  'insurance_card',
  'diabetes_readings',
];

/** `as const satisfies Record<...>` — the same shape `requestLabels.ts`
 *  documents: a literal key per vocabulary member, so every call site reads
 *  a key the typed `t()` overloads (task 19) actually admit. */
const MEDIA_KIND_LABEL_KEY = {
  sensor_photo: 'cases.media.kind.sensor_photo',
  error_photo: 'cases.media.kind.error_photo',
  receipt_photo: 'cases.media.kind.receipt_photo',
  serial_number_photo: 'cases.media.kind.serial_number_photo',
  event_log: 'cases.media.kind.event_log',
  wrong_sms: 'cases.media.kind.wrong_sms',
  wasfati: 'cases.media.kind.wasfati',
  insurance_card: 'cases.media.kind.insurance_card',
  diabetes_readings: 'cases.media.kind.diabetes_readings',
} as const satisfies Record<MediaKind, string>;

/** Displayed intrinsic size of a gallery tile — matches `.thumb`'s CSS
 *  (100% inline-size scaled to a 120px block-size), given as real `width`/
 *  `height` attributes so the browser reserves the box before the
 *  full-resolution file finishes downloading (F-4). */
const THUMB_WIDTH = 160;
const THUMB_HEIGHT = 120;

const FORBIDDEN_CODE = 'forbidden';

export function CaseMedia({ caseUuid, canUpload }: CaseMediaProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<readonly CaseMediaRow[] | null>(null);
  const [kind, setKind] = useState<MediaKind>('sensor_photo');
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  const inFlight = useRef(false);
  // F-1: the one handle onto the native, uncontrolled file input — needed to
  // clear its `.value` after a successful upload, since React state alone
  // (`setFile(null)`) never touches the DOM element the browser itself keeps
  // showing a filename in.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();

  const load = useCallback(async () => {
    setStatus('loading');

    try {
      const found = await mediaApi.list(caseUuid);
      setRows(found);
      setStatus('ready');
    } catch {
      // This panel's own error state — `CaseRequests.tsx`'s own ruling,
      // copied here: a failed fetch must not blank the rest of the case
      // screen.
      setStatus('error');
    }
  }, [caseUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * `CodeUploadPanel.tsx`'s own function, copied rather than reinvented
   * (F-2). Branches on the four non-field codes this app already has
   * shared or case-scoped copy for; otherwise surfaces the API's own first
   * field message, falling back to the generic sentence only when the 422
   * carried no field detail at all.
   */
  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default: {
        const [firstField] = Object.values(error.details);
        return firstField?.[0] ?? t('cases.media.uploadFailed');
      }
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const chosen = event.target.files?.[0];
    if (chosen === undefined) return;
    setFile(chosen);
    setFileName(chosen.name);
    setFailure(null);
    setReference(null);
  }

  async function handleUpload(): Promise<void> {
    if (inFlight.current || file === null) return;
    inFlight.current = true;
    setUploading(true);
    setFailure(null);
    setReference(null);

    try {
      const uploaded = await mediaApi.upload(caseUuid, kind, file);
      // The row the API already answered with, prepended — the same
      // `CaseMessages.tsx` ruling: a refetch would be a second request
      // answering the same question `POST` just answered, and every
      // retrieval through `mediaUrl` is audited, so a refetch would also
      // cost an extra audit row for nothing.
      setRows((current) => [uploaded, ...(current ?? [])]);
      setFile(null);
      setFileName(null);
      // F-1: the DOM element itself, not just the React state above — an
      // uncontrolled `<input type="file">` keeps showing the chosen
      // filename, and keeps its `value` set, until something clears it.
      // Without this, re-selecting the SAME file (one photo legitimately
      // serving two kinds) fires no `change` event at all.
      if (fileInputRef.current !== null) fileInputRef.current.value = '';
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // `kind` is deliberately left as the agent set it — see this
      // module's own docblock.
      setFailure(describeFailure(error));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setUploading(false);
    }
  }

  return (
    <section className={styles['panel']}>
      <CardHead level="h3" icon={<ImageIcon size={16} weight="duotone" />} title={t('cases.media.panelTitle')} />

      {status === 'error' ? (
        <div role="alert" className={styles['empty']}>
          <p>{t('cases.media.loadFailed')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('cases.retry')}
          </Button>
        </div>
      ) : rows === null ? (
        <p role="status">{t('list.loading')}</p>
      ) : rows.length === 0 ? (
        <p className={styles['empty']}>{t('cases.media.noneYet')}</p>
      ) : (
        <ul className={styles['gallery']}>
          {rows.map((row) => {
            // F-8: the date folds into the accessible name so two photos of
            // the same kind (staff uploads do not supersede one another —
            // spec D-5) are not indistinguishable to a screen reader, and so
            // the name is not a bare duplicate of the visible `.itemKind`
            // caption beside it.
            const photoAlt = t('cases.media.photoAlt', {
              kind: t(MEDIA_KIND_LABEL_KEY[row.kind]),
              date: formatTimestamp(row.created_at, i18n.language),
            });

            return (
              <li key={row.uuid} className={styles['item']}>
                {/*
                 * F-4: the ONLY affordance this fix adds. Opens the same
                 * authenticated bytes route as the thumbnail, full
                 * resolution, in a new tab — the browser is the viewer, no
                 * lightbox. The `<img>`'s `alt` is the link's only content,
                 * so it is also the link's accessible name (naming the
                 * kind), and a real `<a href>` is keyboard-reachable by
                 * construction.
                 *
                 * `rel="noopener"`, NEVER `noreferrer`, AND THAT IS LOAD
                 * BEARING. This route is Sanctum-cookie-authenticated, and
                 * `EnsureFrontendRequestsAreStateful::fromFrontend()` decides
                 * whether to run the session pipeline at all from
                 * `referer ?: origin`. `noreferrer` strips the `Referer`
                 * header, and a top-level navigation carries no `Origin`
                 * either — so Sanctum saw neither, skipped the session,
                 * fell through to token auth and answered 401.
                 *
                 * The symptom was exact and confusing: the THUMBNAIL loaded
                 * (an `<img>` subresource does send a `Referer`) while
                 * CLICKING it failed. Reported from production against
                 * `GET /case-media/{uuid}`.
                 *
                 * Withholding the referrer buys nothing here — the
                 * destination is this application's own API, not a third
                 * party. `CentreList.tsx`'s map link keeps `noreferrer`
                 * precisely because that one DOES leave for Google.
                 * `noopener` is the half that carries the security property.
                 */}
                <a
                  className={styles['thumbLink']}
                  href={mediaUrl(row.uuid)}
                  target="_blank"
                  rel="noopener"
                >
                  <img
                    className={styles['thumb']}
                    src={mediaUrl(row.uuid)}
                    width={THUMB_WIDTH}
                    height={THUMB_HEIGHT}
                    alt={photoAlt}
                  />
                </a>
                <div className={styles['itemMeta']}>
                  <span className={styles['itemKind']}>{t(MEDIA_KIND_LABEL_KEY[row.kind])}</span>
                  <span className={styles['itemDate']}>
                    <bdi dir="ltr">{formatTimestamp(row.created_at, i18n.language)}</bdi>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!canUpload ? null : (
        <div className={styles['uploadRow']}>
          {/* ONE row: the photo kind, the file, and Upload at the end, on one
              control baseline — not a full-width kind select stacked over a
              loose file input. */}
          <div className={styles['fileRow']}>
            <div className={styles['kindField']}>
              <Combobox
                label={t('cases.media.kindLabel')}
                width="fill"
                value={kind}
                onChange={(value) => {
                  setKind(value as MediaKind);
                }}
                emptyMessage={t('cases.media.kindNoMatch')}
                options={KIND_OPTIONS.map((option) => ({
                  value: option,
                  label: t(MEDIA_KIND_LABEL_KEY[option]),
                }))}
              />
            </div>

            <FileField
              ref={fileInputRef}
              id={fileInputId}
              label={t('cases.media.fileLabel')}
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              fileName={fileName}
              emptyText={t('cases.media.noFileChosen')}
              onChange={handleFileChange}
            />

            {/*
             * F-5: `disabled` alone, never `disabled={... || uploading}` —
             * `Button.tsx`'s own contract is that an explicit `disabled`
             * outranks `loading`, so combining the two would pull the
             * button out of the tab order at exactly the moment its
             * `aria-busy` is set and render no spinner at all. `loading`
             * carries the busy state on its own; the visible label swap to
             * "Uploading…" below gives sighted users the same feedback.
             */}
            <Button
              loading={uploading}
              disabled={file === null}
              onClick={() => {
                void handleUpload();
              }}
              icon={<UploadSimpleIcon size={16} />}
            >
              {uploading ? t('cases.media.uploading') : t('cases.media.upload')}
            </Button>
          </div>

          <div className={styles['failure']} role="alert" aria-atomic="true">
            {failure === null ? null : <span>{failure}</span>}
            {reference === null ? null : (
              <span className={styles['reference']}>
                {t('auth.supportReference')}{' '}
                <bdi dir="ltr" data-testid="caseMediaRequestId">
                  {reference}
                </bdi>
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
